/**
 * Image Optimization and Compression Utility
 * Resizes and compresses images client-side before uploading to the server.
 * This saves bandwidth, prevents server upload limit errors, and optimizes image load speeds on the frontend.
 */

export interface CompressionResult {
  file: File;
  originalSize: number;
  compressedSize: number;
  ratio: number; // Percentage saved (e.g. 84.5)
  width: number;
  height: number;
}

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0 to 1
  convertTo?: 'image/jpeg' | 'image/png' | 'image/webp';
}

/**
 * Compresses an image file client-side.
 * Falls back to original file if compression fails or input is not an image.
 */
export function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<CompressionResult> {
  const {
    maxWidth = 1200, // Balanced max width for high-resolution retina screens
    maxHeight = 1200,
    quality = 0.8,   // High quality/compression trade-off
    convertTo = 'image/webp' // Superior file size reduction with excellent browser support
  } = options;

  return new Promise((resolve) => {
    // Return early if not an image
    if (!file.type.startsWith('image/')) {
      resolve({
        file,
        originalSize: file.size,
        compressedSize: file.size,
        ratio: 0,
        width: 0,
        height: 0
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        // Calculate aspect ratio resizing
        if (width > maxWidth || height > maxHeight) {
          if (width > height) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          } else {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve({
            file,
            originalSize: file.size,
            compressedSize: file.size,
            ratio: 0,
            width: img.width,
            height: img.height
          });
          return;
        }

        // Fill white background for JPEGs to prevent transparency issues
        if (convertTo === 'image/jpeg') {
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, width, height);
        }

        ctx.drawImage(img, 0, 0, width, height);

        // Determine destination mime type based on browser supports or convertTo opt
        // Note: Canvas.toBlob fallback will default to image/png if webp is unsupported
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              resolve({
                file,
                originalSize: file.size,
                compressedSize: file.size,
                ratio: 0,
                width: img.width,
                height: img.height
              });
              return;
            }

            // Generate optimized extension and name
            let ext = '.png';
            if (convertTo === 'image/webp') ext = '.webp';
            else if (convertTo === 'image/jpeg') ext = '.jpg';

            const baseName = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;
            const compressedFile = new File([blob], `${baseName}-opt-${Date.now()}${ext}`, {
              type: blob.type,
              lastModified: Date.now()
            });

            const originalSize = file.size;
            const compressedSize = compressedFile.size;
            const ratio = originalSize > 0 ? (1 - compressedSize / originalSize) * 100 : 0;

            resolve({
              file: compressedFile,
              originalSize,
              compressedSize,
              ratio: Math.max(0, Number(ratio.toFixed(1))),
              width,
              height
            });
          },
          convertTo,
          quality
        );
      };

      img.onerror = () => {
        resolve({
          file,
          originalSize: file.size,
          compressedSize: file.size,
          ratio: 0,
          width: 0,
          height: 0
        });
      };

      img.src = e.target?.result as string;
    };

    reader.onerror = () => {
      resolve({
        file,
        originalSize: file.size,
        compressedSize: file.size,
        ratio: 0,
        width: 0,
        height: 0
      });
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Format bytes into human readable string
 */
export function formatBytes(bytes: number, decimals = 1): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}
